import { AirBridgeLoader, LOADER_CANVAS } from './AirBridgeLoader';

// FD-276 — what the operator sees before the app exists.
//
// `main.tsx` waits on `msalInstance.initialize()` and `handleRedirectPromise()`
// before it renders anything at all. Until FD-276 that window showed the raw
// `index.html`: a WHITE PAGE, for as long as MSAL took — and, on failure,
// forever, with the only explanation in the browser console.
//
// A blank page is the worst failure mode an operator can be handed, because it
// is indistinguishable from a network problem, an expired session, a bad
// bookmark, and a broken deployment. So this component covers both states: the
// wait, and the wait that never ends.

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-6 px-6 text-center"
      // Opaque, and the theme canvas: the loader must sit on the same ground as
      // the frame must paint exactly that or the resolve state shows a seam.
      style={{ backgroundColor: LOADER_CANVAS }}
    >
      {children}
    </div>
  );
}

export function BootSplash() {
  return (
    <Frame>
      <AirBridgeLoader size={96} label="Starting AirBridge" />
      <p className="font-display text-sm text-[#605E5C]">Signing you in…</p>
    </Frame>
  );
}

/**
 * Which boot step failed. The remedies share nothing, so the copy must not.
 *
 * `auth` — MSAL could not initialize. Look at the Entra app registration.
 * `config` — `/config.json` was missing, unreadable, or invalid (FD-420). Look
 *            at the deployment: the console reads every value it needs from that
 *            file at runtime, and nothing is compiled into the bundle any more.
 */
export type BootFailureCause = 'auth' | 'config';

/**
 * Shown when boot rejects.
 *
 * The message names the causes an operator can actually act on, because
 * "something went wrong" plus a reload button trains people to reload forever.
 * The underlying error is surfaced verbatim rather than summarised: whoever
 * reads this screen is the person who can fix it — and for a configuration
 * failure that verbatim line is what names the missing field.
 *
 * `cause` defaults to `auth`, which is the only failure that existed before
 * FD-420 and keeps every existing call site rendering exactly as it did.
 */
export function BootFailure({
  error,
  cause = 'auth',
}: {
  error: unknown;
  cause?: BootFailureCause;
}) {
  const detail =
    error instanceof Error ? error.message : typeof error === 'string' ? error : null;

  return (
    <Frame>
      <div className="flex max-w-md flex-col gap-3">
        <h1 className="font-display text-lg font-semibold text-[#201F1E]">
          AirBridge could not start
        </h1>
        {cause === 'config' ? (
          <p className="font-display text-sm leading-relaxed text-[#605E5C]">
            The console could not read its configuration. Every value it needs —
            the sign-in app, the authority, the API scope and the list of
            environments — is served next to the bundle as{' '}
            <span className="font-mono">config.json</span>, so this is the
            deployment rather than the sign-in. The detail below names what was
            wrong with it.
          </p>
        ) : (
          <p className="font-display text-sm leading-relaxed text-[#605E5C]">
            Sign-in could not be initialized. This is usually the Entra app
            registration for this deployment — a missing redirect URI, or a
            client or tenant ID that does not match the environment — or an
            identity endpoint this network blocks.
          </p>
        )}
        {detail && (
          <p className="break-words rounded-sm border border-[#E1DFDD] bg-white px-3 py-2 text-left font-mono text-xs text-[#A4262C]">
            {detail}
          </p>
        )}
        <p className="font-display text-xs text-[#A19F9D]">
          Reloading will not help until the configuration is corrected.
        </p>
      </div>
    </Frame>
  );
}
